import { formatToBRL } from "@/utils/formatToMoney"
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover"
import { ScrollArea } from "components/ui/scroll-area"
import { Separator } from "components/ui/separator"
import { format } from "date-fns"
import { ArrowRight, Equal, Eye, GitBranch, MoreHorizontal, Plus, Receipt, Shuffle } from "lucide-react"
import type { IDivergence } from "src/@types/divergence"

type Props = {
    values: IDivergence
    conciliatedDivergences: IDivergence[]
    derivedFromData?: Array<{
        id: number
        name: string
        amount: number
        conciliation_id: number
    }>
    hasConciliatedDivergence: boolean
    hasDerivedFrom: boolean
}

export function SimpleConciliatedDivergencesPopover({
    values,
    conciliatedDivergences,
    derivedFromData = [],
    hasConciliatedDivergence,
    hasDerivedFrom,
}: Props) {
    const totalValue = values.conciliatedDivergence?.conciliated_amount_value
        ? Number(values.conciliatedDivergence.conciliated_amount_value)
        : 0

    const getTotalColor = (value: number) => {
        if (value > 0) return "text-green-600"
        if (value < 0) return "text-red-600"
        return "text-gray-600"
    }

    const getButtonText = () => {
        if (hasConciliatedDivergence && hasDerivedFrom) {
            return `${conciliatedDivergences.length + derivedFromData.length} itens`
        }
        if (hasConciliatedDivergence) {
            return `${conciliatedDivergences.length} itens`
        }
        if (hasDerivedFrom) {
            return `${derivedFromData.length} derivados`
        }
        return "0 itens"
    }

    const getButtonIcon = () => {
        if (hasConciliatedDivergence && hasDerivedFrom) {
            return <Shuffle className="w-3.5 h-3.5" />
        }
        if (hasConciliatedDivergence) {
            return <Receipt className="w-3.5 h-3.5" />
        }
        if (hasDerivedFrom) {
            return <GitBranch className="w-3.5 h-3.5" />
        }
        return <Receipt className="w-3.5 h-3.5" />
    }

    const allValues = [
        ...(Array.isArray(conciliatedDivergences) ? conciliatedDivergences.map((d) => Number(d.amount)) : []),
        Number(values.amount),
    ]

    const maxValuesShown = 3
    const showLimitedCalculation = allValues.length > maxValuesShown + 1
    const visibleValues = showLimitedCalculation
        ? [...allValues.slice(0, maxValuesShown), Number(values.amount)]
        : allValues
    const hiddenCount = showLimitedCalculation ? allValues.length - maxValuesShown - 1 : 0

    return (
        <Popover modal={true}>
            <PopoverTrigger asChild>
                <button
                    className="w-full group flex justify-center items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:text-[#402430] bg-white hover:bg-gray-50 rounded-md border border-gray-200 hover:border-[#402430] transition-all duration-200"
                    type="button"
                >
                    {getButtonIcon()}
                    <span className="font-medium">
                        {getButtonText()}
                    </span>
                    <Eye className="w-3 h-3 opacity-40 group-hover:opacity-70 transition-opacity" />
                </button>
            </PopoverTrigger>

            <PopoverContent className="w-96 p-0 shadow-xl border border-gray-200 bg-white rounded-lg overflow-hidden max-h-[500px] flex flex-col">
                {/* Header */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-sm text-gray-900">Informações de Conciliação</h4>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            ID: {values.conciliatedDivergence?.id || values.id || "-"}
                        </span>
                    </div>
                </div>

                <ScrollArea className="flex-1 overflow-auto">
                    <div className="p-4 space-y-4">
                        {/* Lista de divergências conciliadas */}
                        {hasConciliatedDivergence && (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <Receipt className="w-4 h-4 text-blue-600" />
                                    <h5 className="text-sm font-medium text-gray-900">
                                        Valor Abatido com ({conciliatedDivergences.length} {conciliatedDivergences.length === 1 ? 'item' : 'itens'})
                                    </h5>
                                </div>

                                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                    {conciliatedDivergences.map((divergence) => (
                                        <div
                                            key={divergence.id}
                                            className="flex justify-between items-start gap-3 p-2 bg-blue-50 rounded-md border border-blue-100"
                                        >
                                            <div className="flex flex-col min-w-0 flex-1">
                                                <span
                                                    className="text-sm text-gray-900 font-medium break-words leading-tight"
                                                    title={divergence.name}
                                                    style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                                                >
                                                    {divergence.name}
                                                </span>
                                                {divergence.conciliation_date && (
                                                    <span className="text-xs text-gray-500 mt-0.5">
                                                        {format(new Date(divergence.conciliation_date), "dd/MM/yyyy")}
                                                    </span>
                                                )}
                                                <span className="text-xs text-blue-600 mt-0.5">
                                                    ID: {divergence.id}
                                                </span>
                                            </div>
                                            <span className="text-sm font-medium text-gray-900 whitespace-nowrap flex-shrink-0 ml-2">
                                                {formatToBRL(Number(divergence.amount))}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Lista de divergências derivadas */}
                        {hasDerivedFrom && (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <GitBranch className="w-4 h-4 text-purple-600" />
                                    <h5 className="text-sm font-medium text-gray-900">
                                        Derivado de ({derivedFromData.length} {derivedFromData.length === 1 ? 'item' : 'itens'})
                                    </h5>
                                </div>

                                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                    {derivedFromData.map((derivedItem) => (
                                        <div
                                            key={`derived-${derivedItem.id}`}
                                            className="flex justify-between items-start gap-3 p-2 bg-purple-50 rounded-md border border-purple-100"
                                        >
                                            <div className="flex flex-col min-w-0 flex-1">
                                                <span
                                                    className="text-sm text-gray-900 font-medium break-words leading-tight"
                                                    title={derivedItem.name}
                                                    style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                                                >
                                                    {derivedItem.name}
                                                </span>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className="text-xs text-purple-600">
                                                        ID: {derivedItem.id}
                                                    </span>
                                                    <span className="text-xs text-gray-400">•</span>
                                                    <span className="text-xs text-gray-500">
                                                        Conciliação: {derivedItem.conciliation_id}
                                                    </span>
                                                </div>
                                            </div>
                                            <span className="text-sm font-medium text-gray-900 whitespace-nowrap flex-shrink-0 ml-2">
                                                {formatToBRL(Number(derivedItem.amount))}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {(hasConciliatedDivergence || hasDerivedFrom) && <Separator />}

                        {/* Divergência atual */}
                        <div className="p-3 bg-[#402430]/5 rounded-md border border-[#402430]/20">
                            <div className="flex items-center gap-2 mb-2">
                                <ArrowRight className="w-4 h-4 text-[#402430]" />
                                <span className="text-sm font-medium text-[#402430]">Esta Divergência</span>
                            </div>
                            <div className="flex justify-between items-start gap-3">
                                <div className="flex flex-col min-w-0 flex-1">
                                    <span
                                        className="text-sm text-gray-700 break-words leading-tight"
                                        title={values.name}
                                        style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                                    >
                                        {values.name}
                                    </span>
                                    <span className="text-xs text-gray-500 mt-0.5">
                                        ID: {values.id}
                                    </span>
                                </div>
                                <span className="text-sm font-medium text-[#402430] whitespace-nowrap flex-shrink-0 ml-2">
                                    {formatToBRL(Number(values.amount))}
                                </span>
                            </div>
                        </div>

                        {/* Cálculo apenas para conciliações */}
                        {hasConciliatedDivergence && (
                            <>
                                <Separator />
                                <div className="space-y-2">
                                    <div className="flex items-center justify-center">
                                        <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">Cálculo da Conciliação</span>
                                    </div>

                                    <div className="flex items-center justify-center gap-1 flex-wrap">
                                        {visibleValues.slice(0, -1).map((value, index) => (
                                            <div key={index} className="flex items-center gap-1">
                                                <span className="text-xs font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                                                    {formatToBRL(value)}
                                                </span>
                                                <Plus className="w-2 h-2 text-gray-400" />
                                            </div>
                                        ))}

                                        {hiddenCount > 0 && (
                                            <div className="flex items-center gap-1">
                                                <div className="flex items-center gap-0.5 bg-gray-100 px-1.5 py-0.5 rounded">
                                                    <MoreHorizontal className="w-3 h-3 text-gray-500" />
                                                    <span className="text-xs text-gray-500">+{hiddenCount}</span>
                                                </div>
                                                <Plus className="w-2 h-2 text-gray-400" />
                                            </div>
                                        )}

                                        <span className="text-xs font-medium text-[#402430] bg-[#402430]/10 px-1.5 py-0.5 rounded">
                                            {formatToBRL(Number(values.amount))}
                                        </span>

                                        <Equal className="w-3 h-3 text-gray-400 mx-1" />

                                        <span className={`font-bold text-sm ${getTotalColor(totalValue)} bg-white border px-2 py-1 rounded`}>
                                            {formatToBRL(totalValue)}
                                        </span>
                                    </div>

                                    {hiddenCount > 0 && (
                                        <div className="text-center">
                                            <span className="text-xs text-gray-400">
                                                {hiddenCount} item{hiddenCount > 1 ? "s" : ""} oculto{hiddenCount > 1 ? "s" : ""} no cálculo
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </ScrollArea>
            </PopoverContent>
        </Popover>
    )
}
